import React, { useState, useRef, useEffect } from 'react';
import { Renderer, Stave, StaveNote, Formatter, Accidental } from 'vexflow';

const MusicStaffDropZone = ({ onDrop, width = 500, height = 150, highlightedKey = null, clef = 'treble' }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [placedSymbols, setPlacedSymbols] = useState([]);
  const [ghostPitch, setGhostPitch] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const dropZoneRef = useRef(null);
  const staffContainerRef = useRef(null);
  const staveRef = useRef(null);

  // Pitch map (ledger lines included) - similar to HTML implementation
  const pitches = [
    "c/2", "d/2", "e/2", "f/2", "g/2", "a/2", "b/2",
    "c/3", "d/3", "e/3", "f/3", "g/3", "a/3", "b/3",
    "c/4", "d/4", "e/4", "f/4", "g/4", "a/4", "b/4",
    "c/5", "d/5", "e/5", "f/5", "g/5", "a/5", "b/5",
    "c/6", "d/6", "e/6", "f/6", "g/6", "a/6", "b/6",
  ];

  // Decide stem direction based on pitch and clef
  const getStemDirection = (pitch, clefType = clef) => {
    const [note, octaveStr] = pitch.split("/");
    const octave = parseInt(octaveStr, 10);
    const middle = clefType === "treble" 
      ? { note: "b", octave: 4 } 
      : { note: "d", octave: 3 };

    // Convert to a comparable index
    const order = ["c", "d", "e", "f", "g", "a", "b"];
    const idx = octave * 7 + order.indexOf(note[0].toLowerCase());
    const midIdx = middle.octave * 7 + order.indexOf(middle.note);

    return idx < midIdx ? 1 : -1; // 1 = stem up, -1 = stem down
  };

  // Build quick lookup for pitch indices
  const pitchToIndex = (() => {
    const map = {};
    for (let i = 0; i < pitches.length; i++) {
      map[pitches[i]] = i;
    }
    return map;
  })();

  // Snap Y position to nearest pitch using stave metrics per clef
  const snapToPitch = (y) => {
    // Ensure we have the stave to reference exact line positions
    const stave = staveRef.current;
    if (!stave) {
      // Fallback to previous rough mapping (should rarely happen)
      const roughStep = Math.round((height - y) / 10) + (clef === 'bass' ? 7 : 12);
      const roughIndex = Math.min(Math.max(roughStep, 0), pitches.length - 1);
      return pitches[roughIndex];
    }

    // VexFlow line indexing: 0 = top line, 4 = bottom line
    // Anchor on bottom line for consistency
    const bottomLineY = stave.getYForLine(4);

    // Define the anchor pitch at the bottom line per clef
    // Treble bottom line is E4 (e/4), Bass bottom line is G2 (g/2)
    const anchorPitch = clef === 'bass' ? 'g/2' : 'e/4';
    const anchorIndex = pitchToIndex[anchorPitch] ?? 0;

    // Compute pixels per diatonic step from actual stave line spacing
    // One line-to-next-line = 2 steps
    const lineSpacing = Math.abs(bottomLineY - stave.getYForLine(3));
    const pixelsPerStep = lineSpacing / 2 || 5;

    // Compute how many steps above the bottom line the y is (positive upwards)
    let stepsFromBottom = Math.round((bottomLineY - y) / pixelsPerStep);

    // Fine adjustment: visual alignment in bass clef tended to be ~2 steps low
    const clefFineOffsetSteps = clef === 'bass' ? 2 : 0;
    stepsFromBottom += clefFineOffsetSteps;
    const targetIndex = Math.min(
      Math.max(anchorIndex + stepsFromBottom, 0),
      pitches.length - 1
    );
    return pitches[targetIndex];
  };

  // VexFlow drawing helpers
  const renderStaffAndNotes = () => {
    if (!staffContainerRef.current) return;

    // Clear previous render
    staffContainerRef.current.innerHTML = '';

    // Initialize renderer
    const renderer = new Renderer(staffContainerRef.current, Renderer.Backends.SVG);
    renderer.resize(width, height);
    const context = renderer.getContext();

    // Create and draw stave
    const horizontalPadding = 10;
    const stave = new Stave(horizontalPadding, 10, Math.max(0, width - horizontalPadding * 2));
    stave.addClef(clef).setContext(context).draw();
    staveRef.current = stave;

    // Build vexflow notes from placed symbols that have a computed vexKey
    const vfNotes = placedSymbols
      .filter(s => s.vexKey && s.renderAsNote !== false)
      .map(s => {
        const duration = s.duration || 'q';
        const note = new StaveNote({ keys: [s.vexKey], duration, clef });
        
        // Add accidental if present
        if (s.accidental) {
          note.addModifier(new Accidental(s.accidental));
        }
        
        // Set stem direction based on pitch
        const stemDirection = getStemDirection(s.vexKey, clef);
        note.setStemDirection(stemDirection);
        
        return note;
      });

    // Draw placed notes
    if (vfNotes.length > 0) {
      Formatter.FormatAndDraw(context, stave, vfNotes);
    }

    // Draw ghost note if dragging
    if (ghostPitch && isDragging) {
      const ghost = new StaveNote({
        clef,
        keys: [ghostPitch],
        duration: 'q',
        stem_direction: getStemDirection(ghostPitch, clef),
      });
      ghost.setStyle({ fillStyle: "rgba(128, 128, 128, 0.6)", strokeStyle: "rgba(128, 128, 128, 0.8)" });
      Formatter.FormatAndDraw(context, stave, [ghost]);
    }
  };

  // Map a y-position to the nearest vexflow key like 'c/4'
  const yToVexKey = (y) => {
    // Use the snapToPitch function for consistent behavior
    return snapToPitch(y);
  };

  // Handle drag over event with ghost position
  const handleDragOver = (e) => {
    e.preventDefault(); // Allow drop
    if (!isDragOver) {
      setIsDragOver(true);
    }

    // Show ghost position during drag
    if (isDragging) {
      const rect = dropZoneRef.current.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const pitch = snapToPitch(y);
      setGhostPitch(pitch);
    }
  };

  // Handle drag leave event
  const handleDragLeave = () => {
    setIsDragOver(false);
    setGhostPitch(null);
  };

  // Handle drop event with improved positioning
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    setIsDragging(false);
    setGhostPitch(null);

    try {
      // Get the dropped symbol data
      const symbolData = JSON.parse(e.dataTransfer.getData('application/json'));

      // Get drop coordinates relative to the staff
      const rect = dropZoneRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Map Y to nearest musical key using snapToPitch
      const vexKey = snapToPitch(y);

      // Determine note duration/accidental from symbol data when applicable
      let duration = 'q';
      let accidental = null;
      if (symbolData && symbolData.id) {
        if (symbolData.id === 'whole_note') duration = 'w';
        else if (symbolData.id === 'half_note') duration = 'h';
        else if (symbolData.id === 'quarter_note') duration = 'q';
        else if (symbolData.id === 'eighth_note') duration = '8';
        else if (symbolData.id === 'sharp') accidental = '#';
        else if (symbolData.id === 'flat') accidental = 'b';
        else if (symbolData.id === 'natural') accidental = 'n';
      }

      // Decide whether to render as a note in VexFlow (skip clefs/rests as glyphs here)
      let renderAsNote = true;
      if (symbolData && (symbolData.id === 'treble_clef' || symbolData.id === 'rest')) {
        renderAsNote = false;
      }

      // Create a new placed symbol
      const newSymbol = {
        ...symbolData,
        id: `${symbolData.id}-${Date.now()}`,
        x,
        y,
        vexKey,
        duration,
        accidental,
        renderAsNote
      };

      // Add to placed symbols and re-render
      setPlacedSymbols(prev => [...prev, newSymbol]);

      // Call the onDrop callback if provided
      if (onDrop) {
        onDrop(symbolData, { x, y, vexKey });
      }
    } catch (error) {
      console.error('Error processing dropped item:', error);
    }
  };

  // Handle drag start from external sources
  const handleDragStart = () => {
    setIsDragging(true);
  };

  // Handle drag end from external sources
  const handleDragEnd = () => {
    setIsDragging(false);
    setGhostPitch(null);
  };

  // Clear all placed symbols
  const clearSymbols = () => {
    setPlacedSymbols([]);
  };

  // Clear last 5 placed symbols when answer is wrong
  const clearLastFiveSymbols = () => {
    setPlacedSymbols(prev => prev.slice(0, Math.max(0, prev.length - 5)));
  };

  // Expose the clearSymbols method and handle external drag events
  useEffect(() => {
    if (dropZoneRef.current) {
      dropZoneRef.current.clearSymbols = clearSymbols;
      dropZoneRef.current.clearLastFiveSymbols = clearLastFiveSymbols;
      
      // Listen for drag events from external sources
      const handleGlobalDragStart = () => handleDragStart();
      const handleGlobalDragEnd = () => handleDragEnd();
      
      document.addEventListener('dragstart', handleGlobalDragStart);
      document.addEventListener('dragend', handleGlobalDragEnd);
      
      return () => {
        document.removeEventListener('dragstart', handleGlobalDragStart);
        document.removeEventListener('dragend', handleGlobalDragEnd);
      };
    }
  }, []);

  // Initial render and whenever size, placed symbols, ghost state, or clef changes
  useEffect(() => {
    renderStaffAndNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, height, placedSymbols, ghostPitch, isDragging, clef]);

  return (
    <div 
      ref={dropZoneRef}
      className={`staff-drop-zone ${isDragOver ? 'drag-over' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{ 
        position: 'relative', 
        width, 
        height,
        border: isDragOver ? '2px dashed #007bff' : '1px solid #ddd',
        borderRadius: '8px',
        backgroundColor: isDragOver ? 'rgba(0, 123, 255, 0.05)' : 'transparent',
        transition: 'all 0.2s ease'
      }}
    >
      <div ref={staffContainerRef} style={{ width: '100%', height: '100%' }} />
      
      {/* Instructions overlay
      {!isDragging && placedSymbols.length === 0 && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: '#999',
          fontSize: '14px',
          textAlign: 'center',
          pointerEvents: 'none'
        }}>
          Drag musical symbols here
        </div>
      )} */}
    </div>
  );
};

export default MusicStaffDropZone;